import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/core/lib/apiClient';
import { useSession } from '@/hooks/useSession';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import { Button } from '@/ui';
import type { Capabilities, Page } from './contracts';
import { networks, errorCopy } from './service';

export function ProfileChannel({ handle }: { handle: string }) {
  const { currentUser } = useSession();
  const load = useCallback(async () => {
    const caps = await networks.capabilities();
    return caps.flags?.creator_collaboration
      ? api<Page<'channels'>>(`/channels/?owner=${encodeURIComponent(handle)}`)
      : null;
  }, [handle]);
  const result = useAsyncRead<Page<'channels'> | null>(load, currentUser?.id ?? 'guest', null);
  if (!result.data) return null;
  return (
    <>
      {result.data.items.map((r) => (
        <p key={r.id}>
          <Link to={`/channels/${encodeURIComponent(r.slug)}`}>Creator channel: {r.title}</Link>
        </p>
      ))}
    </>
  );
}
export function DiscussionGuideEntry({ postId }: { postId: string }) {
  const { currentUser } = useSession();
  const result = useAsyncRead<Capabilities | null>(
    networks.capabilities,
    currentUser?.id ?? 'guest',
    null,
  );
  if (!currentUser || !result.data?.flags?.collections_guides) return null;
  return <GuideButton key={`${currentUser.id}:${postId}`} postId={postId} />;
}
function GuideButton({ postId }: { postId: string }) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const navigate = useNavigate(),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return (
    <div>
      <Button
        variant="ghost"
        disabled={busy}
        onClick={() => {
          if (busy) return;
          setBusy(true);
          setError('');
          void networks
            .create('guides', {
              title: 'Guide from discussion',
              description: '',
              visibility: 'private',
              data: {
                state: 'draft',
                blocks: [
                  {
                    id: crypto.randomUUID(),
                    kind: 'source',
                    text: '',
                    consent: 'not_required',
                    source: { postId, title: '', href: '', available: true },
                  },
                ],
              },
            })
            .then(
              (r) => {
                if (alive.current) navigate(`/guides/${encodeURIComponent(r.slug)}`);
              },
              (e) => {
                if (alive.current) setError(errorCopy(e));
              },
            )
            .finally(() => {
              if (alive.current) setBusy(false);
            });
        }}
      >
        Create private guide draft from this discussion
      </Button>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
